import { defineStore } from 'pinia';
import type {
    MissionV1 as MissionV1,
    ListMissionsQueryParams
} from '~/api/v1/missions/MissionRepository';

import type {
    Mission,
    CreateMissionPayload,
    MissionResponse, UpdateMissionPayload, GetMissionQueryParams,
} from '~/api/v2/missions/MissionRepository';

export const useMissionStoreV1 = defineStore('mission-v1', {
    state: () => ({
        missions: [] as MissionV1[],
        mission: null as Mission | null,
        totalMissions: 0,
        isLoading: false,
        error: null as string | null,
        pagination: null
    }),

    getters: {
        getMissions: (state) => state.missions,
        getTotalMissions: (state) => state.totalMissions,
    },

    actions: {
        async fetchMissions(params: ListMissionsQueryParams = {}) {
            this.missions = [];
            try {
                this.isLoading = true;
                this.error = null;

                const { $api } = useNuxtApp();
                const response = await $api().v1.missions.fetchAllMissions(params);

                this.missions = response.missions;
                this.totalMissions = response.total;

                return response;
            } catch (error) {
                console.error('Error fetching missions:', error);
                this.error = error instanceof Error ? error.message : 'Failed to fetch missions';
                throw error;
            } finally {
                this.isLoading = false;
            }
        },

        createMission(payload: CreateMissionPayload, companyId?: string) {
            const { $api } = useNuxtApp();
            return $api().v2.missions.create(payload, companyId).then((response: MissionResponse) => {

                this.mission = response

                return response
            })
        },

        updateMission(mission_id: string, payload: UpdateMissionPayload = {}) {
            const { $api } = useNuxtApp();

            return $api().v2.missions.update(mission_id, payload).then((response: MissionResponse) => {

                this.mission = response

                return response
            })
        },

        async fetchOneMission(missionId: string, params: GetMissionQueryParams = {}) {
            const { $api } = useNuxtApp();
            return $api().v2.missions.getOneById(missionId, params).then((response: MissionResponse) => {
                this.mission = response;
                return response
            });
        },

        async deleteMission(missionId: string) {
            try {
                this.isLoading = true;
                this.error = null;

                const { $api } = useNuxtApp();
                await $api().v1.missions.deleteMission(missionId);

                // Remove mission from store after successful deletion
                // We cancel the mission but we don't delete it so we don't have to hide it as we handle status
                //this.missions = this.missions.filter(mission => mission.mission_id !== missionId);

                return true;
            } catch (error) {
                console.error(`Error deleting mission ${missionId}:`, error);
                this.error = error instanceof Error ? error.message : `Failed to delete mission ${missionId}`;
                throw error;
            } finally {
                this.isLoading = false;
            }
        },

        async fillMission(missionId: string) {
            try {
                this.isLoading = true;
                this.error = null;

                const { $api } = useNuxtApp();
                await $api().v1.missions.fillMission(missionId);

                return true;
            } catch (error) {
                console.error(`Error filling mission ${missionId}:`, error);
                this.error = error instanceof Error ? error.message : `Failed to fill mission ${missionId}`;
                throw error;
            } finally {
                this.isLoading = false;
            }
        },

        async deleteMissions(missionIds: string[]) {
            this.isLoading = true;
            this.error = null;
            try {
                for (const missionId of missionIds) {
                    await this.deleteMission(missionId);
                }
                return true;
            } catch (error) {
                console.error(`Error deleting missions:`, error);
                this.error = error instanceof Error ? error.message : `Failed to delete missions`;
                throw error;
            } finally {
                this.isLoading = false;
            }
        },

        async updateMissionFeedback(token: string, payload: UpdateMissionFeedbackPayload) {
            try {
                this.isLoading = true;
                this.error = null;
                const { $api } = useNuxtApp();
                await $api().v2.missions.updateMissionFeedback(token, payload);
                return true;
            } catch (error) {
                console.error(`Error updating mission feedback:`, error);
                this.error = error instanceof Error ? error.message : `Failed to update mission feedback`;
                throw error;
            } finally {
                this.isLoading = false;
            }
        },

        async fetchMissionByFeedbackToken(token: string) {
            try {
                this.isLoading = true;
                this.error = null;
                const { $api } = useNuxtApp();

                // The endpoint now returns the complete experience with feedbacks, experience_type, and creator
                const response = await $api().v2.mission_feedback.fetchByToken(token);

                // Extract the experience (flattened), feedbacks, experience_type, creator, and completion fields
                const { feedbacks, current_feedback, experience_type, creator, mission_status, completion_requested_at, ...experience } = response;

                // Use the feedback that THIS token points to (the recipient's own row).
                // Never pick feedbacks[0]: an experience can hold several testimonials and
                // that would pre-fill the form with another recipient's comment.
                const currentFeedback = current_feedback ?? null;

                let freelancerName = 'N/A';
                let freelancerPictureUrl: string | null = null;

                // For declarative experiences, use creator info from the API response
                // For proven experiences, fetch invitations to get freelancer info
                if (experience_type === 'declarative' && creator) {
                    // Declarative experience: use created_by (creator) info
                    freelancerName = `${creator.first_name} ${creator.last_name}`;
                    freelancerPictureUrl = creator.picture_url;
                } else {
                    // Proven experience: try to fetch invitations to get freelancer name and profile picture
                    // This may fail on public pages (no auth), so we gracefully handle the error
                    try {
                        const invitations = await $api().v1.invitations.fetchInvitations(experience.mission_id);
                        const acceptedInvitation = invitations.find(inv => inv.status === 'accepted');
                        if (acceptedInvitation) {
                            freelancerName = `${acceptedInvitation.user.first_name} ${acceptedInvitation.user.last_name}`;
                            freelancerPictureUrl = acceptedInvitation.user?.profile?.picture_url ?? null;
                        }
                    } catch (e) {
                        // On public pages, invitation fetch may fail (401) - continue without freelancer info
                        console.warn('Could not fetch invitations (public page):', e);
                    }
                }

                return {
                    mission: experience,
                    missionFeedback: currentFeedback || { score: 0, public_comment: null, private_comment: null },
                    freelancerName,
                    freelancerPictureUrl,
                    experienceType: experience_type || 'declarative',
                    missionStatus: mission_status || '',
                    completionRequestedAt: completion_requested_at || null,
                };
            } catch (error) {
                console.error(`Error fetching mission by feedback token:`, error);
                this.error = error instanceof Error ? error.message : `Failed to fetch mission by feedback token`;
                throw error;
            } finally {
                this.isLoading = false;
            }
        },
    },
});